import type { Skill } from 'bundles/browse-common/types/Skill';

export function toSkillUrl(skill: Skill) {
  return `/courses?query=${encodeURIComponent(skill.skillName.toLowerCase())}`;
}

// We exclude skills that have <= 4 related courses.
const excludedSkills = [
  'accident-reporting',
  'adjudication',
  'adobe-acrobat',
  'adobe-analytics',
  'adobe-experience-manager',
  'advertising-mail',
  'apple-service-certification',
  'arterial-blood-gas-analysis',
  'artifactory',
  'as-built-drawings',
  'atlassian-bamboo',
  'autocad-civil-3d',
  'automated-clearing-house',
  'balancing-ledger/billing',
  'bartending',
  'basic-life-support-bls-certification',
  'bed-management',
  'behavioral-health-case-management',
  'blood-banking',
  'blueprinting',
  'breakage-control',
  'business-objects-framework',
  'business-support-systems',
  'capillary',
  'cardiac-catheterization',
  'cardiac-stress-testing',
  'cardiac-surgery',
  'cash-control',
  'cash-handling',
  'cash-receipts',
  'cashiering',
  'cavity-prevention',
  'cesarean-section',
  'chest-physiotherapy',
  'choral-music',
  'clinical-research-coordination',
  'clinical-social-work',
  'clinical-trial-management-systems',
  'common-core-standards',
  'companionship',
  'computer-aided-three-dimensional-interactive-application-catia',
  'computerized-physician-order-entry',
  'contract-accounting',
  'cpt-coding',
  'dayforce-hcm-software',
  'defibrillation',
  'dental-experience',
  'dental-informatics',
  'dental-radiography',
  'diagnostic-medical-sonography',
  'diatetics',
  'discharge-planning',
  'dispatching',
  'echocardiography',
  'eight-disciplines-problem-solving-8d',
  'electronic-benefits-transfer',
  'emergency-nursing',
  'engineering-change-order',
  'epic-emr',
  'fall-protection',
  'family-and-medical-leave-act-of-1993',
  'family-medicine',
  'federal-information-security-management-act',
  'fluoroscopy',
  'force-dot-com',
  'friendliness',
  'general-lending',
  'good-driving-record',
  'hazardous-waste-operations-and-emergency-response-standard-hazwoper',
  'home-health-care-and-assisted-living',
  'hotel-and-restaurant-management',
  'insurance-and-warranty-claims-processing',
  'interrogatories',
  'interventional-radiology',
  'inventory-staging',
  'invision-design-software',
  'iso-13485-standard',
  'it-general-controls-itgc',
  'iwork',
  'jury-trial',
  'knowledge-of-apple-hardware',
  'knowledge-of-apple-software',
  'labview',
  'law-practice-management-software',
  'legal-depositions',
  'legal-pleadings',
  'legal-support',
  'life-support',
  'lifting-ability',
  'loan-documentation',
  'loan-origination',
  'loss-prevention',
  'mammography',
  'medical-assistance',
  'medical-social-work',
  'medical-support',
  'medical-telemetry',
  'merchandise-maintenance',
  'microstrategy',
  'midwifery',
  'motion-arguments',
  'okta',
  'operational-data-store',
  'operations-support-systems',
  'oral-cancer-screening',
  'oral-motor-skills',
  'order-entry',
  'other-mobile-programming-languages',
  'patient-coordination',
  'patient-transfer-equipment',
  'pediatric-first-aid',
  'perl-programming-language',
  'personal-injury-law',
  'pharmacy-experience',
  'phlebotomy',
  'positive-airway-pressure-cpap-bipap',
  'post-anesthesia-care-unit',
  'practice-management-software',
  'pre-anesthetic-assessment',
  'procurement-cards',
  'product-family-engineering',
  'product-flow-diagram',
  'property-inspection',
  'pulmonary-function-testing',
  'punch-work',
  'recruitment-process-outsourcing',
  'regulatory-documents',
  'resource-consumption-accounting',
  'salesforce-object-query-language-soql',
  'sap-analytics-cloud',
  'sap-signavio',
  'securities-sales',
  'shop-drawing',
  'specimen-labeling',
  'speech-therapy',
  'splinting',
  'sql-server-reporting-services',
  'stock-rotation',
  'submittals-construction',
  'supplier-quality-management',
  'swallowing-disorders',
  'telemarketing',
  'tenacity',
  'trial-preparation',
  'ultipro-hris-software',
  'urgent-care',
  'venipuncture',
  'visualforce',
  'westlaw',
];

export function isNotExcludedSkill(skillId: string) {
  return !excludedSkills.includes(skillId);
}

// Popular queries that we want to display skill pages for, but can't redirect directly to the skill page because the queries are being used for SEO.
// This mapping allows us to show the appropriate skill page when these specific queries are made.
// Data massaged from the edge redirects list: https://github.com/webedx-spark/edge/blob/main/redirects/domains/global/eqpRedirects.ts
// and matched with skills from Skills Taxonomy 2.0
export const seoQueriesToSkills: Record<string, string> = {
  'data structures and algorithms': 'data-structure',
  agile: 'agile-methodology',
  'android app development': 'android-software-development',
  angular: 'angular',
  bootstrap: 'bootstrap-front-end-framework',
  'c programming': 'c-programming-language',
  'c++': 'cplusplus',
  coding: 'computer-programming',
  'communication skills': 'communication',
  'computer networking': 'computer-network',
  'creative writing': 'writing',
  'data modeling': 'data-model',
  'data science toolkit': 'data-analysis',
  'data warehouse': 'data-warehousing',
  development: 'software-development',
  'image processing': 'image-analysis',
  'distributed systems': 'distributed-computing-architecture',
  docker: 'docker-software',
  'problem solving': 'complex-problem-solving',
  'english writing': 'writing',
  'essay writing': 'writing',
  'financial markets': 'financial-market',
  fintech: 'financial-technology',
  food: 'food-and-beverage',
  'full stack web development': 'full-stack-web-development',
  'game development': 'video-game-development',
  'genome sequencing': 'bioinformatics',
  genomics: 'bioinformatics',
  gcp: 'google-cloud-platform',
  'english grammar': 'grammar',
  mapreduce: 'apache-hadoop',
  hadoop: 'apache-hadoop',
  'small business': 'entrepreneurship',
  'hotel management': 'hospitality-management',
  computer: 'computer-science',
  programming: 'computer-programming',
  startup: 'entrepreneurship',
  'html css': 'html-and-css',
  html: 'hypertext-markup-language-html',
  hr: 'human-resources',
  'ux design': 'user-centered-design',
  investment: 'investments',
  'ios app development': 'ios',
  'ios developer': 'ios',
  iot: 'internet-of-things',
  'mobile app development': 'mobile-application-development',
  java: 'java-programming-language',
  'life coaching': 'coaching',
  'life science': 'life-sciences',
  macroeconomics: 'economics',
  'marketing strategy': 'marketing-strategy-and-techniques',
  'data analysis excel': 'excel-macros',
  microsoft: 'microsoft-365',
  'microsoft office': 'microsoft-365',
  music: 'media-production',
  networking: 'computer-network',
  'neural networks': 'artificial-neural-network',
  'node js': 'node-js',
  'object oriented programming': 'object-oriented-programming-oop',
  'python pandas': 'pandas-python-package',
  pharmaceutical: 'pharmaceuticals',
  php: 'php-scripting-language',
  'pivot table': 'pivot-tables-and-charts',
  pmi: 'project-management-institute-pmi-methodology',
  'presentation skills': 'presentations',
  'pricing strategy': 'marketing-strategy-and-techniques',
  'strategic planning': 'marketing-strategy-and-techniques',
  'programming basics': 'computer-programming',
  nlp: 'natural-language-processing',
  'python data science': 'python-programming-language',
  'qualitative research methods': 'qualitative-research',
  'r programming': 'r-programming-language',
  'r statistics': 'r-programming-language',
  rstudio: 'r-programming-language',
  'relational database': 'relational-databases',
  safety: 'safety-assurance',
  sales: 'general-sales-practices',
  scala: 'scala-programming-language',
  'scikit learn': 'scikit-learn-machine-learning-library',
  seo: 'search-engine-optimization',
  'stock market': 'financial-market',
  valuation: 'business-valuation',
  swift: 'swift-programming-language',
  'system biology': 'biology',
  'transact sql': 'transact-sql',
  tableau: 'tableau-software',
  'teaching english': 'teaching',
  tesol: 'teaching',
  'time series analysis': 'time-series-analysis-and-forecasting',
  transport: 'transportation-operations',
  'uml diagram': 'unified-modeling-language',
  ui: 'user-interface-ui',
  'ux research': 'ui-ux-research',
  'video games': 'animation-and-game-design',
  'visual basic': 'visual-basic-programming-language',
  'data visualization': 'visualization-computer-graphics',
  'web application': 'web-applications',
  python: 'python-programming-language',
  'app development': 'mobile-application-development',
  'react js': 'react-javascript-library',
  ruby: 'ruby-programming-language',
};
